import { PropsWithChildren, useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Box, Button, Divider, Typography, useMediaQuery } from '@mui/material'
import { Card } from '../Card'
import { PropsWithWizard } from '../Wizard'
import { ContentTitle } from '../ContentTitle'
import { useContract } from '~/contexts'
import { ActiveBy, ContractStepper } from './components'
import { ContractStatus } from '../ContractStatus'
import { ContractCanCreateInvoicesDialog } from '../ContractCanCreateInvoicesDialog'
import { formatDate, theme } from '~/utils'
import { IconChevronLeft } from '@tabler/icons-react'
import { EContractStatus } from '~/graphql/types'

export type ContractFlowLayoutProps = {
  nextButtonLabel?: string
}
export const ContractFlowLayout: React.FC<PropsWithChildren<PropsWithWizard<ContractFlowLayoutProps>>> = (props) => {
  const { children, goToPage } = props
  const { contract, resetContract } = useContract()
  const { state } = useLocation()
  const { contractId } = useParams()
  const [canCreateInvoicesModalIsOpen, setCanCreateInvoicesModalIsOpen] = useState(false)

  const isLowerXs = useMediaQuery('(max-width:500px)')
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))

  const isIndicationPartner = Boolean(contract?.indicationRef?.partner?.name)
  const isIndicationCustomer = Boolean(contract?.customerIndicationRef?.customerName)
  const shouldShowCanCreateInvoicesText = contract?.status === EContractStatus.inDeactivation || contract?.status === EContractStatus.cancellationRequested || contract?.status === EContractStatus.deactivated

  useEffect(() => {
    if (!contractId) {
      resetContract()
      goToPage(0)
    }
    state?.newProposalCreated && goToPage(1)
    window.history.replaceState({}, document.title)
  }, [contractId])

  return (
    <Card>
      {isLowerMd ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexFlow: isLowerXs ? 'column' : 'row', gap: isLowerXs ? '1rem' : '0' }}>
            <Link to='/app/contracts'>
              <Button size='small' sx={{ margin: '0' }} color='secondary'>
                <IconChevronLeft />
              </Button>
            </Link>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {contract && <ContractStatus status={contract.status} />}
              {contract && shouldShowCanCreateInvoicesText && (
                <Typography 
                  variant='body2' 
                  color='primary'
                  onClick={() => setCanCreateInvoicesModalIsOpen(true)}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {contract.canCreateInvoices ? 'Permite criar novas faturas' : 'Não permite criar novas faturas'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.6rem', paddingTop: '1rem' }}>
            <Typography color={theme.palette.grey[400]} fontSize='.9rem'>Gerada em: {formatDate(contract?.createdAt)} {!isIndicationCustomer && !isIndicationPartner && '| por: Desperta'}</Typography>
            {isIndicationPartner && (
              <Box>
                <Typography variant='body2' color='grey.400'>por: <b>{contract?.indicationRef?.partner?.name}</b></Typography>
                <Typography variant='body2' color='grey.400'>Origem: <b>Parceiro</b></Typography>
              </Box>
            )}
            {isIndicationCustomer && (
              <Box>
                <Typography variant='body2' color='grey.400'>por: <b>{contract?.customerIndicationRef?.customerName}</b></Typography>
                <Typography variant='body2' color='grey.400'>Origem: <b>Cliente</b></Typography>
              </Box>
            )}
            {contract?.activatedAt &&
              <ActiveBy />
            }
          </Box>

          <Divider sx={{ marginTop: 4, marginBottom: 4 }} />

          <ContentTitle
            style={{ padding: '0px 0px 24px 0px' }}
            title={contract ? 'Acompanhar contrato' : 'Gerar contrato'}
            breadcrumbLinks={{ currentLink: 'Gerar contrato', links: [{ href: '/app/contracts', label: 'Contratos' }] }}
            description='Complete os dados para formalizar o contrato'
          />
        </>
      ) : (
        <ContentTitle
          style={{ padding: '0px 0px 24px 0px' }}
          title={contract ? 'Acompanhar contrato' : 'Gerar contrato'}
          rightContent={contract ? (
            <>
              <Box sx={{ display: 'flex', flexFlow: 'column', gap: '.6rem', paddingTop: '1rem', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
                    <Typography color={theme.palette.grey[400]} fontSize='.9rem'>Status do contrato: </Typography>
                    <ContractStatus status={contract.status} />
                  </Box>
                  {shouldShowCanCreateInvoicesText && (
                    <Typography 
                      variant='body2' 
                      color='#3BA1FF'
                      onClick={() => setCanCreateInvoicesModalIsOpen(true)}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'right' }}
                    >
                      {contract.canCreateInvoices ? <><b>PERMITE</b> criar novas faturas</> : <><b>NÃO PERMITE</b> criar novas faturas</>}
                    </Typography>
                  )}
                </Box>
                <Typography color={theme.palette.grey[400]} fontSize='.9rem'>Gerada em: {formatDate(contract?.createdAt)} {!isIndicationCustomer && !isIndicationPartner && '| por: Desperta'}</Typography>
                {isIndicationPartner && (
                  <Box>
                    <Typography variant='body2' color='grey.400'>por: <b>{contract?.indicationRef?.partner?.name}</b></Typography>
                    <Typography variant='body2' color='grey.400'>Origem: <b>Parceiro</b></Typography>
                  </Box>
                )}
                {isIndicationCustomer && (
                  <Box>
                    <Typography variant='body2' color='grey.400'>por: <b>{contract?.customerIndicationRef?.customerName}</b></Typography>
                    <Typography variant='body2' color='grey.400'>Origem: <b>Cliente</b></Typography>
                  </Box>
                )}
                {contract?.activatedAt &&
                  <ActiveBy />
                }
              </Box>
            </>
          ) : undefined
          }
          breadcrumbLinks={{ currentLink: 'Gerar contrato', links: [{ href: '/app/contracts', label: 'Contratos' }] }}
          description='Complete os dados para formalizar o contrato'
        />
      )}

      <Divider sx={{ marginBottom: 5 }} />

      {/* {contract?.status === EContractStatus.sent ? 
        <ContractTabs contract={contract} {...props} />
        :  */}

      <ContractStepper contract={contract} {...props} />

      {/* } */}

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {children}
      </Box>

      <ContractCanCreateInvoicesDialog isVisible={canCreateInvoicesModalIsOpen} toggleModal={() => setCanCreateInvoicesModalIsOpen(false)} />
    </Card>
  )
}
